import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from 'react-query';
import { AuthProvider } from './contexts/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import ArtworksList from './pages/ArtworksList';
import ArchiveList from './pages/ArchiveList';
import Lists from './pages/Lists';
import ArtworkDetail from './pages/ArtworkDetail';
import ArtworkForm from './pages/ArtworkForm';
import Reports from './pages/Reports';
import AuthorsList from './pages/AuthorsList';
import AdminPanel from './pages/AdminPanel';
import FundsAdmin from './pages/FundsAdmin';
import UsersPage from './pages/UsersPage';
import PrivateRoute from './components/PrivateRoute';
import RoleRoute from './components/RoleRoute';

const theme = createTheme({
  palette: {
    primary: {
      main: '#68998C', // Основной цвет
      light: '#9ACBBF', // Светлый вариант основного
      dark: '#57726C', // Темный вариант основного
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#648991', // Вторичный цвет A
      light: '#99BFC7', // Светлый вариант вторичного A
      dark: '#54686D', // Темный вариант вторичного A
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#FFFFFF', // Белый фон для основной части окна
      paper: '#FFFFFF',
    },
    text: {
      primary: '#5A5A5A',
      secondary: '#57726C', // Темный вариант основного для вторичного текста
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#68998C',
          color: '#FFFFFF',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          '&.MuiTypography-h6': {
            color: '#5A5A5A',
          },
        },
      },
    },
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <Layout />
                  </PrivateRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="artworks" element={<ArtworksList />} />
                <Route
                  path="archive"
                  element={
                    <RoleRoute permission="view_archive">
                      <ArchiveList />
                    </RoleRoute>
                  }
                />
                <Route
                  path="lists"
                  element={
                    <RoleRoute permission="export_documents">
                      <Lists />
                    </RoleRoute>
                  }
                />
                <Route
                  path="artworks/new"
                  element={
                    <RoleRoute permission="create_artworks">
                      <ArtworkForm />
                    </RoleRoute>
                  }
                />
                <Route path="artworks/:id" element={<ArtworkDetail />} />
                <Route
                  path="artworks/:id/edit"
                  element={
                    <RoleRoute permission="edit_artworks">
                      <ArtworkForm />
                    </RoleRoute>
                  }
                />
                <Route
                  path="authors"
                  element={
                    <RoleRoute permission="view_authors">
                      <AuthorsList />
                    </RoleRoute>
                  }
                />
                <Route
                  path="reports"
                  element={
                    <RoleRoute permission="view_reports">
                      <Reports />
                    </RoleRoute>
                  }
                />
                <Route
                  path="admin"
                  element={
                    <RoleRoute roles={['admin']}>
                      <AdminPanel />
                    </RoleRoute>
                  }
                />
                <Route
                  path="admin/funds"
                  element={
                    <RoleRoute permission="manage_funds">
                      <FundsAdmin />
                    </RoleRoute>
                  }
                />
                <Route
                  path="users"
                  element={
                    <RoleRoute roles={['owner']}>
                      <UsersPage />
                    </RoleRoute>
                  }
                />
              </Route>
            </Routes>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;

